import { useCallback, useState } from 'react';
import { useRMDispatch, useRMSelector } from 'redux/hooks';
import {
  NotificationProgressStatus,
  setNotification,
} from 'redux/reducers/notificationReducer';
import { selectShowProgress } from 'redux/selectors/StateSelectors';

export interface IndeterminateProgressMessages {
  inProgressMessage: string;
  doneMessage: string;
  failedMessage: string;
}

// Hook to execute something and report its progress using progress bar (indeterminate), with error handling as well
export const useExecuteWithIndeterminateProgress = (
  action: () => Promise<void>,
  { inProgressMessage, doneMessage, failedMessage }: IndeterminateProgressMessages,
) => {
  const dispatch = useRMDispatch();
  const isGlobalProgressShown = useRMSelector(selectShowProgress);
  const [isExecuting, setIsExecuting] = useState(false);

  const execute = useCallback(async () => {
    if (isExecuting) {
      return;
    }

    const ownsGlobalProgress = !isGlobalProgressShown;
    setIsExecuting(true);
    if (ownsGlobalProgress) {
      dispatch(
        setNotification({
          showProgress: true,
          message: inProgressMessage,
          progressStatus: NotificationProgressStatus.InProgress,
          progress: -1,
        }),
      );
    }

    try {
      await action();
      if (ownsGlobalProgress) {
        dispatch(
          setNotification({
            showProgress: false,
            progress: 100,
            message: doneMessage,
            progressStatus: NotificationProgressStatus.Done,
          }),
        );
      }
    } catch (error) {
      console.error(error);
      if (ownsGlobalProgress) {
        dispatch(
          setNotification({
            showProgress: false,
            message: failedMessage,
            progressStatus: NotificationProgressStatus.Error,
          }),
        );
      }
    } finally {
      setIsExecuting(false);
    }
  }, [
    isExecuting,
    isGlobalProgressShown,
    action,
    dispatch,
    inProgressMessage,
    doneMessage,
    failedMessage,
  ]);

  return { isExecuting, execute };
};
